import { useCallback, useMemo } from 'react';
import type { AuthenticatedBranch } from '@optiflow/types';
import { useAuthStore } from '@/store/authStore';
import { queryClient } from '@/services/queryClient';

export interface UseBranchResult {
  branches: AuthenticatedBranch[];
  activeBranch: AuthenticatedBranch | null;
  canSwitch: boolean;
  selectBranch: (branchId: string) => void;
}

export function useBranch(): UseBranchResult {
  const branches = useAuthStore((state) => state.user?.branches);
  const activeBranchId = useAuthStore((state) => state.activeBranchId);
  const setActiveBranch = useAuthStore((state) => state.setActiveBranch);

  const list = useMemo(() => branches ?? [], [branches]);

  const selectBranch = useCallback(
    (branchId: string) => {
      // Guard the local switch too, so a stale id from storage cannot be re-selected.
      if (!list.some((branch) => branch.id === branchId)) return;

      setActiveBranch(branchId);
      // Branch-scoped data must not survive a switch, or a user sees the previous branch's rows.
      void queryClient.invalidateQueries();
    },
    [list, setActiveBranch],
  );

  return {
    branches: list,
    activeBranch: list.find((branch) => branch.id === activeBranchId) ?? null,
    canSwitch: list.length > 1,
    selectBranch,
  };
}
